import { useState, useRef} from 'react';
import MapView from './MapView';
import { Coordinates, CalculationMethod, PrayerTimes } from 'adhan';
import MeccaLoading from './MeccaLoading';
import muslimData from './muslimData';

// Helper function to format numbers with K and M
const formatNumber = (numberStr) => {
  // Remove commas and convert to number
  const num = parseInt(numberStr.replace(/,/g, ''));
  
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return num.toString();
};

// Create the countries array from muslimData
const countries = Object.keys(muslimData).map(countryId => ({
  id: countryId,
  name: getCountryName(countryId),
  description: getCountryDescription(countryId),
  totalMuslims: muslimData[countryId].totalMuslims,
  formattedMuslims: formatNumber(muslimData[countryId].totalMuslims),
  totalMosques: muslimData[countryId].totalMosques,
  formattedMosques: formatNumber(muslimData[countryId].totalMosques),
  totalQuranicSchools: muslimData[countryId].totalQuranicSchools,
  formattedQuranicSchools: formatNumber(muslimData[countryId].totalQuranicSchools),
  states: muslimData[countryId].states.map(state => ({
    ...state,
    formattedMuslimPopulation: formatNumber(state.muslimPopulation),
    formattedMosques: formatNumber(state.mosques)
  }))
}));

function getCountryName(countryId) {
  const countryNames = {
    'usa': 'United States',
    'canada': 'Canada',
    'uk': 'United Kingdom',
    'australia': 'Australia',
    'newzealand': 'New Zealand'
  };
  return countryNames[countryId] || countryId;
}

function getCountryDescription(countryId) {
  const descriptions = {
    'usa': 'Prayer times across 50 states',
    'canada': 'Prayer times across provinces',
    'uk': 'Prayer times across UK regions',
    'australia': 'Prayer times across states and territories',
    'newzealand': 'Prayer times across regions'
  };
  return descriptions[countryId] || 'Prayer times information';
}

// Europe card data
const europeCard = {
  id: 'europe',
  name: 'Europe',
  flag: '🇪🇺',
  description: 'Pray for Muslims across Europe',
  externalLink: 'https://share.google/K1yg2q8nNBPESe7rQ',
  note: 'Support European Muslim communities',
  buttonText: 'Visit Prayer Network',
  color: 'from-blue-500 to-blue-600'
};

// Function to calculate prayer times using Adhan library
const calculatePrayerTimes = (latitude, longitude, timezone) => {
  try {
    const coordinates = new Coordinates(latitude, longitude);
    const date = new Date();
    
    // Use North America calculation method (ISNA)
    const params = CalculationMethod.NorthAmerica();
    
    const prayerTimes = new PrayerTimes(coordinates, date, params);
    
    // Format the times to local timezone
    const formatTime = (date) => {
      return date.toLocaleTimeString('en-US', {
        timeZone: timezone,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    };
    
    return {
      fajr: formatTime(prayerTimes.fajr),
      sunrise: formatTime(prayerTimes.sunrise),
      dhuhr: formatTime(prayerTimes.dhuhr),
      asr: formatTime(prayerTimes.asr),
      maghrib: formatTime(prayerTimes.maghrib),
      isha: formatTime(prayerTimes.isha)
    };
  } catch (error) {
    console.error('Error calculating prayer times:', error);
    // Fallback to approximate times if calculation fails
    return getApproximateTimes(timezone);
  }
};

// Fallback function for approximate times
const getApproximateTimes = (timezone) => {
  // Adjust base times based on timezone
  const timezoneOffset = getTimezoneOffset(timezone);
  
  const formatTime = (hour, minute) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
  };
  
  // Approximate prayer times (adjust based on hemisphere)
  const isSouthernHemisphere = ['Australia', 'Pacific'].some(zone => timezone.includes(zone));
  const seasonOffset = isSouthernHemisphere ? -1 : 0; // Adjust for southern hemisphere seasons
  
  return {
    fajr: formatTime(5 + timezoneOffset + seasonOffset, 30),
    sunrise: formatTime(6 + timezoneOffset + seasonOffset, 45),
    dhuhr: formatTime(13 + timezoneOffset, 15),
    asr: formatTime(16 + timezoneOffset, 30),
    maghrib: formatTime(19 + timezoneOffset + seasonOffset, 15),
    isha: formatTime(20 + timezoneOffset + seasonOffset, 45)
  };
};

const getTimezoneOffset = (timezone) => {
  const timezoneOffsetMap = {
    // USA & Canada
    'America/New_York': 0,
    'America/Chicago': -1,
    'America/Denver': -2,
    'America/Los_Angeles': -3,
    'America/Anchorage': -4,
    'Pacific/Honolulu': -5,
    'America/Phoenix': -2,
    'America/Detroit': 0,
    'America/Indiana/Indianapolis': 0,
    'America/Boise': -2,
    'America/Toronto': 0,
    'America/Vancouver': -3,
    'America/Edmonton': -2,
    'America/Winnipeg': -1,
    'America/Halifax': 1,
    'America/St_Johns': 1.5,
    'America/Montreal': 0,
    'America/Regina': -1,
    
    // UK
    'Europe/London': 0,
    
    // Australia
    'Australia/Sydney': 10,
    'Australia/Melbourne': 10,
    'Australia/Brisbane': 10,
    'Australia/Adelaide': 9.5,
    'Australia/Perth': 8,
    'Australia/Darwin': 9.5,
    'Australia/Hobart': 10,
    
    // New Zealand
    'Pacific/Auckland': 12,
    'Pacific/Chatham': 12.75
  };
  
  return timezoneOffsetMap[timezone] || 0;
};

function CountrySelector() {
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [prayerTimes, setPrayerTimes] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Create ref for scrolling to states section
  const statesSectionRef = useRef(null);

  const handleCountryClick = (country) => {
    setSelectedCountry(country);
    setSearchTerm('');
    setSelectedState(null);
    setPrayerTimes(null);
    
    // Scroll to states section after a short delay to ensure DOM update
    setTimeout(() => {
      if (statesSectionRef.current) {
        statesSectionRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start'
        });
      }
    }, 100);
  };

  const handleStateClick = (country, state) => {
    if (selectedState && selectedState.state.name === state.name) {
      return;
    }
    
    setSelectedState({ country, state });
    setLoading(true);
    
    setTimeout(() => {
      const times = calculatePrayerTimes(state.lat, state.lng, state.timezone);
      setPrayerTimes(times);
      setLoading(false);
    }, 500);
  };

  const handleBackToStates = () => {
    setSelectedState(null);
    setPrayerTimes(null);
    
    // Scroll back to states section
    setTimeout(() => {
      if (statesSectionRef.current) {
        statesSectionRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start'
        });
      }
    }, 100);
  };

  const handleBackToCountries = () => {
    setSelectedCountry(null);
    setSelectedState(null);
    setPrayerTimes(null);
    
    // Scroll to top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStateSelectFromMap = (stateName) => {
    if (selectedCountry && stateName) {
      const stateObj = selectedCountry.states.find(s => s.name === stateName);
      if (stateObj) {
        if (selectedState && selectedState.state.name === stateObj.name) {
          return;
        }
        
        setSelectedState({ country: selectedCountry, state: stateObj });
        setLoading(true);
        
        setTimeout(() => {
          const times = calculatePrayerTimes(stateObj.lat, stateObj.lng, stateObj.timezone);
          setPrayerTimes(times);
          setLoading(false);
        }, 500);
      }
    }
  };

  const handleEuropeClick = () => {
    window.open(europeCard.externalLink, '_blank', 'noopener noreferrer');
  };

  const filteredStates = selectedCountry 
    ? selectedCountry.states.filter(state => 
        state.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        state.code.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  // Helper functions
  const getFlagEmoji = (countryId) => {
    const flags = {
      'usa': '🇺🇸',
      'canada': '🇨🇦',
      'uk': '🇬🇧',
      'australia': '🇦🇺',
      'newzealand': '🇳🇿',
      'europe': '🇪🇺'
    };
    return flags[countryId] || '🏳️';
  };

  const getRegionType = (countryId) => {
    const types = {
      'usa': 'states',
      'canada': 'provinces',
      'uk': 'regions',
      'australia': 'states',
      'newzealand': 'regions'
    };
    return types[countryId] || 'regions';
  };

  const getCountryAbbreviation = (countryId) => {
    const abbreviations = {
      'usa': 'USA',
      'canada': 'Canada',
      'uk': 'UK',
      'australia': 'Australia',
      'newzealand': 'NZ'
    };
    return abbreviations[countryId] || countryId.toUpperCase();
  };

  const getTimezoneLabel = (timezone) => {
    const labels = {
      // USA & Canada
      'America/New_York': 'Eastern Time (ET)',
      'America/Chicago': 'Central Time (CT)',
      'America/Denver': 'Mountain Time (MT)',
      'America/Los_Angeles': 'Pacific Time (PT)',
      'America/Anchorage': 'Alaska Time (AKT)',
      'Pacific/Honolulu': 'Hawaii Time (HT)',
      'America/Phoenix': 'Mountain Time (MST)',
      'America/Detroit': 'Eastern Time (ET)',
      'America/Indiana/Indianapolis': 'Eastern Time (ET)',
      'America/Boise': 'Mountain Time (MT)',
      'America/Toronto': 'Eastern Time (ET)',
      'America/Vancouver': 'Pacific Time (PT)',
      'America/Edmonton': 'Mountain Time (MT)',
      'America/Winnipeg': 'Central Time (CT)',
      'America/Halifax': 'Atlantic Time (AT)',
      'America/St_Johns': 'Newfoundland Time (NT)',
      'America/Montreal': 'Eastern Time (ET)',
      'America/Regina': 'Central Time (CST)',
      
      // UK
      'Europe/London': 'Greenwich Mean Time (GMT)',
      
      // Australia
      'Australia/Sydney': 'Australian Eastern Time (AET)',
      'Australia/Melbourne': 'Australian Eastern Time (AET)',
      'Australia/Brisbane': 'Australian Eastern Time (AET)',
      'Australia/Adelaide': 'Australian Central Time (ACT)',
      'Australia/Perth': 'Australian Western Time (AWT)',
      'Australia/Darwin': 'Australian Central Time (ACT)',
      'Australia/Hobart': 'Australian Eastern Time (AET)',
      
      // New Zealand
      'Pacific/Auckland': 'New Zealand Time (NZT)',
      'Pacific/Chatham': 'Chatham Time (CHAST)'
    };
    return labels[timezone] || 'Local Time';
  };

  // State Details View
  if (selectedState) {
    const { country, state } = selectedState;
    const timezoneLabel = getTimezoneLabel(state.timezone);
    const countryAbbreviation = getCountryAbbreviation(country.id);

    return (
      <div className="w-full max-w-6xl mx-auto animate-fadeIn">
        <button 
          onClick={handleBackToStates}
          className="mb-6 text-yellow-600 hover:text-yellow-700 flex items-center text-lg"
        >
          ← Back to {country.name}
        </button>
        
        {/* Map Section */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-3xl font-bold text-gray-900">
                  {state.name}, {countryAbbreviation}
                </h2>
                <p className="text-gray-600 mt-1">
                  Location Map • {timezoneLabel}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Coordinates</p>
                <p className="font-mono text-sm">{state.lat.toFixed(4)}, {state.lng.toFixed(4)}</p>
              </div>
            </div>
            
            <MapView 
              country={country.id} 
              state={state.name} 
              onStateSelect={handleStateSelectFromMap}
              states={selectedCountry.states}
            />
            
            <div className="mt-4 flex justify-center space-x-4 text-sm text-gray-600">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-yellow-600 rounded-full mr-2"></div>
                <span>🕌 Click on any {getRegionType(country.id)} marker to view prayer times</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Prayer Times Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="mb-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Today's Prayer Times</h3>
                <p className="text-gray-600">
                  {new Date().toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric',
                    timeZone: state.timezone
                  })}
                </p>
              </div>
              <div className="mt-2 md:mt-0">
                <span className="inline-block px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                  {timezoneLabel}
                </span>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="py-12">
              <MeccaLoading />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {prayerTimes && Object.entries(prayerTimes).map(([prayer, time]) => (
                  <div key={prayer} className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-white rounded-xl p-4 prayer-card">
                    <div className="mb-3 text-center text-2xl">
                      {prayer === 'fajr' ? '🌅' : 
                       prayer === 'sunrise' ? '☀️' : 
                       prayer === 'dhuhr' ? '🕌' : 
                       prayer === 'asr' ? '☀️' : 
                       prayer === 'maghrib' ? '🌇' : 
                       '🌙'}
                    </div>
                    <h4 className="font-semibold text-center capitalize text-sm mb-1">
                      {prayer === 'asr' ? 'Asr' : 
                       prayer === 'dhuhr' ? 'Dhuhr' : 
                       prayer === 'maghrib' ? 'Maghrib' : 
                       prayer === 'isha' ? 'Isha' : 
                       prayer.charAt(0).toUpperCase() + prayer.slice(1)}
                    </h4>
                    <p className="text-xl font-bold text-center">{time}</p>
                    <p className="text-xs text-center opacity-90 mt-1">Local Time</p>
                  </div>
                ))}
              </div>

              {/* Additional Info */}
              <div className="mt-8 p-6 bg-yellow-50 rounded-xl">
                <h4 className="font-semibold text-gray-900 mb-4">Prayer Time Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Calculation Method</p>
                    <p className="font-medium">Islamic Society of North America (ISNA)</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Time Zone</p>
                    <p className="font-medium">{timezoneLabel}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{getRegionType(country.id).slice(0, -1)} Code</p>
                    <p className="font-medium">{state.code}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Coordinates</p>
                    <p className="font-mono text-sm">{state.lat.toFixed(4)}, {state.lng.toFixed(4)}</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-yellow-200">
                  <p className="text-sm text-gray-600">
                    Note: Prayer times are calculated based on astronomical formulas and may vary by a few minutes. 
                    Always consult your local mosque for exact prayer times.
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Muslim Community Information Section */}
        <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 rounded-2xl shadow-lg p-8 mb-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
            <span className="mr-3">🕌</span>
            Muslim Community Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="text-3xl text-yellow-600 mb-2">👥</div>
              <p className="text-sm text-gray-500 mb-1">Muslim Population</p>
              <p className="text-2xl font-bold text-gray-900">{state.muslimPopulation}</p>
              <p className="text-xs text-gray-400 mt-1">Approximate estimate</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="text-3xl text-yellow-600 mb-2">🕌</div>
              <p className="text-sm text-gray-500 mb-1">Mosques</p>
              <p className="text-2xl font-bold text-gray-900">{state.mosques}</p>
              <p className="text-xs text-gray-400 mt-1">Registered places of worship</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="text-3xl text-yellow-600 mb-2">📚</div>
              <p className="text-sm text-gray-500 mb-1">Quranic Schools</p>
              <p className="text-2xl font-bold text-gray-900">{state.quranicSchools}</p>
              <p className="text-xs text-gray-400 mt-1">Islamic education centers</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 text-center shadow-md">
              <div className="text-3xl text-yellow-600 mb-2">📈</div>
              <p className="text-sm text-gray-500 mb-1">Community Size</p>
              <p className="text-xl font-bold text-gray-900">
                {state.muslimPopulation && parseInt(state.muslimPopulation.replace(/,/g, '')) > 100000 ? 'Large' : 
                 parseInt(state.muslimPopulation.replace(/,/g, '')) > 25000 ? 'Medium' : 'Small'}
              </p>
              <p className="text-xs text-gray-400 mt-1">Relative to country</p>
            </div>
          </div>
          
          {/* Major Mosques Section */}
          {state.majorMosques && state.majorMosques.length > 0 && (
            <div className="mb-8">
              <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <span className="mr-2">⭐</span>
                Major Mosques & Islamic Centers
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {state.majorMosques.map((mosque, index) => (
                  <div key={index} className="bg-white rounded-lg p-4 border border-yellow-200 hover:border-yellow-400 transition-colors">
                    <div className="flex items-start">
                      <div className="bg-yellow-100 p-2 rounded-lg mr-3">
                        <span className="text-yellow-600">🕌</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{mosque}</p>
                        <p className="text-sm text-gray-500 mt-1">Islamic place of worship</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Community Notes */}
          {state.notes && (
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
              <div className="flex">
                <div className="mr-3 text-yellow-600">💡</div>
                <div>
                  <p className="font-medium text-gray-900 mb-1">Community Notes</p>
                  <p className="text-gray-700">{state.notes}</p>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Country Summary */}
        <div className="p-6 bg-gradient-to-r from-yellow-600 to-yellow-700 text-white rounded-2xl">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <div>
              <h4 className="text-xl font-bold mb-2">{country.name} - Muslim Community Summary</h4>
              <p className="text-yellow-100">
                Total Muslims: {country.totalMuslims} • Mosques: {country.totalMosques} • Quranic Schools: {country.totalQuranicSchools}
              </p>
            </div>
            <div className="mt-4 md:mt-0">
              <span className="inline-block px-4 py-2 bg-white text-yellow-700 rounded-lg font-medium">
                {countryAbbreviation} • Islamic Community
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-6xl mx-auto">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold text-gray-900 mb-4">
          Select Your Country
        </h2>
        <p className="text-gray-600 text-lg">
          Click on a flag to view prayer times and Muslim community information
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6 mb-12">
        {/* First row: Regular countries */}
        {countries.map((country) => (
          <div
            key={country.id}
            className="flex flex-col items-center cursor-pointer"
            onClick={() => handleCountryClick(country)}
          >
            <div className="p-6 rounded-2xl bg-white shadow-lg hover:shadow-xl transition-shadow w-full h-full">
              <div className="flex items-center justify-center mb-4">
                <div className="text-6xl">
                  {getFlagEmoji(country.id)}
                </div>
              </div>
              
              <div className="text-center mb-4">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{country.name}</h3>
                <p className="text-gray-500 text-sm mb-3">{country.description}</p>
              </div>
              
              {/* Country stats with K/M formatting */}
              <div className="flex justify-between items-center mb-4">
                <div className="text-center flex-1">
                  <p className="text-xl font-bold text-yellow-600" title={country.totalMuslims}>
                    {country.formattedMuslims}
                  </p>
                  <p className="text-xs text-gray-500">Muslims</p>
                </div>
                <div className="w-px h-8 bg-gray-200"></div>
                <div className="text-center flex-1">
                  <p className="text-xl font-bold text-gray-600" title={country.totalMosques}>
                    {country.formattedMosques}
                  </p>
                  <p className="text-xs text-gray-500">Mosques</p>
                </div>
                <div className="w-px h-8 bg-gray-200"></div>
                <div className="text-center flex-1">
                  <p className="text-xl font-bold text-yellow-600" title={country.totalQuranicSchools}>
                    {country.formattedQuranicSchools}
                  </p>
                  <p className="text-xs text-gray-500">Schools</p>
                </div>
              </div>
              
              <div className="text-center pt-3 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  {country.states.length} {getRegionType(country.id)}
                </p>
              </div>
            </div>
          </div>
        ))}
        
        {/* Europe Card - Standalone at the end */}
        <div
          key={europeCard.id}
          className="flex flex-col items-center cursor-pointer col-span-full md:col-span-1"
          onClick={handleEuropeClick}
        >
          <div className={`p-6 rounded-2xl bg-gradient-to-br ${europeCard.color} text-white shadow-lg hover:shadow-xl transition-shadow w-full h-full`}>
            <div className="flex items-center justify-center mb-4">
              <div className="text-6xl">
                {europeCard.flag}
              </div>
            </div>
            
            <div className="text-center mb-4">
              <h3 className="text-2xl font-bold text-white mb-2">{europeCard.name}</h3>
              <p className="text-blue-100 text-sm mb-3">{europeCard.description}</p>
              <p className="text-blue-200 text-xs mb-4 italic">
                "If you want to pray for Muslims in Europe, join our prayer network"
              </p>
            </div>
            
            <div className="text-center mb-4">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 rounded-full mb-3">
                <span className="text-xl">🙏</span>
              </div>
              <p className="text-sm text-blue-100">
                Click to visit the European Muslim Prayer Network
              </p>
            </div>
            
            <div className="text-center pt-4 border-t border-blue-400/30">
              <button className="px-6 py-3 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-colors w-full">
                {europeCard.buttonText} →
              </button>
            </div>
            
            <div className="mt-4 text-center">
              <p className="text-xs text-blue-200">
                External link: {europeCard.externalLink}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* States Section with ref for scrolling */}
      <div ref={statesSectionRef}>
        {selectedCountry && (
          <div className="mt-12 p-8 bg-white rounded-2xl shadow-lg animate-fadeIn">
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">
                  {selectedCountry.name} - {getRegionType(selectedCountry.id).charAt(0).toUpperCase() + getRegionType(selectedCountry.id).slice(1)}
                </h3>
                <p className="text-gray-600 mt-2">
                  Select a {getRegionType(selectedCountry.id).slice(0, -1)} to view accurate prayer times and Muslim community data
                </p>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder={`Search ${getRegionType(selectedCountry.id)}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent w-64"
                  />
                  <span className="absolute right-3 top-2.5 text-gray-400">🔍</span>
                </div>
                
                <button
                  onClick={handleBackToCountries}
                  className="px-4 py-2 text-gray-500 hover:text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  ✕ Change Country
                </button>
              </div>
            </div>

            {filteredStates.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {filteredStates.map((state) => (
                  <div
                    key={state.code}
                    onClick={() => handleStateClick(selectedCountry, state)}
                    className="p-4 border border-gray-200 rounded-xl hover:border-yellow-500 hover:bg-yellow-50 cursor-pointer transition-all group"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 group-hover:text-yellow-700 truncate">
                          {state.name}
                        </h4>
                        <p className="text-sm text-gray-500">{state.code}</p>
                        
                        {/* State stats with K/M formatting and better spacing */}
                        <div className="flex items-center mt-2 space-x-3">
                          <div className="flex items-center space-x-1">
                            <span className="text-yellow-600 text-xs">👥</span>
                            <span className="text-xs font-bold text-yellow-700" title={`${state.muslimPopulation} Muslims`}>
                              {state.formattedMuslimPopulation}
                            </span>
                            <span className="text-xs text-gray-500">Muslims</span>
                          </div>
                          <div className="w-px h-3 bg-gray-300"></div>
                          <div className="flex items-center space-x-1">
                            <span className="text-gray-600 text-xs">🕌</span>
                            <span className="text-xs font-bold text-gray-700" title={`${state.mosques} Mosques`}>
                              {state.formattedMosques}
                            </span>
                            <span className="text-xs text-gray-500">Mosques</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-yellow-600 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                        →
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-gray-400 truncate">
                      Click for prayer times & community data
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">
                  No {getRegionType(selectedCountry.id)} found matching "{searchTerm}"
                </p>
              </div>
            )}

            <div className="mt-6 text-sm text-gray-500 text-center">
              Showing {filteredStates.length} of {selectedCountry.states.length} {getRegionType(selectedCountry.id)}
              {searchTerm && ` matching "${searchTerm}"`}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CountrySelector;